// Client-side batch download + zip for the picker.
//
// The picker SPA and the asset bytes are served from the SAME origin in
// production (https://mktgassets.moldex.com/ + https://mktgassets.moldex.com/cdn/),
// so the browser can fetch the bytes and build a zip locally — no server work.
// We always fetch by *pathname* (e.g. "/cdn/<file>") rather than the manifest's
// absolute URL: in production that resolves to the same origin, and in local dev
// it hits Vite's /cdn proxy (see vite.config.ts) so downloads are testable too.

import { zip, type Zippable } from "fflate";
import type { ManifestAsset, ManifestVariant } from "./types.js";

/** A single file to include in a download (one variant of one asset). */
export interface DownloadItem {
  asset: ManifestAsset;
  variant: ManifestVariant;
  /** Collision-free filename used inside the zip / for the direct download. */
  filename: string;
}

export interface DownloadProgress {
  done: number;
  total: number;
}

export interface DownloadResult {
  /** Files successfully delivered (in the zip, or the single direct download). */
  delivered: number;
  /** Items whose bytes failed to fetch. */
  failed: { filename: string; error: string }[];
  /** True if the run was aborted (no zip was saved). */
  aborted?: boolean;
}

const FETCH_CONCURRENCY = 5;

/**
 * Run a batch download. If there's exactly one file we stream it straight to the
 * browser via an anchor (no in-memory buffering — matters for big originals).
 * Two or more files are fetched, stored into a single zip, and saved.
 */
export async function runDownload(
  items: DownloadItem[],
  zipName: string,
  onProgress: (p: DownloadProgress) => void,
  signal?: AbortSignal,
): Promise<DownloadResult> {
  if (items.length === 0) return { delivered: 0, failed: [] };

  const single = items.length === 1 ? items[0] : undefined;
  if (single) {
    // Single file: let the browser download it directly. Same-origin pathname,
    // so the `download` filename hint is honored.
    onProgress({ done: 0, total: 1 });
    triggerDownload(toFetchUrl(single.variant.url), single.filename);
    onProgress({ done: 1, total: 1 });
    return { delivered: 1, failed: [] };
  }

  const total = items.length;
  let done = 0;
  const fetched: (Uint8Array | null)[] = new Array(total).fill(null);
  const failed: DownloadResult["failed"] = [];

  let cursor = 0;
  async function worker() {
    while (cursor < total) {
      const i = cursor++;
      const item = items[i];
      if (!item) continue;
      try {
        fetched[i] = await fetchBytes(item.variant.url, signal);
      } catch (e) {
        failed.push({ filename: item.filename, error: errMsg(e) });
        fetched[i] = null;
      }
      done += 1;
      onProgress({ done, total });
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(FETCH_CONCURRENCY, total) }, () => worker()),
  );

  // Cancelled mid-flight: don't hand the user a partial zip.
  if (signal?.aborted) return { delivered: 0, failed, aborted: true };

  const zippable: Zippable = {};
  let delivered = 0;
  for (let i = 0; i < total; i++) {
    const bytes = fetched[i];
    const item = items[i];
    if (!bytes || !item) continue;
    // level 0 = store. Images/webp are already compressed, so deflate would burn
    // CPU for ~no gain. Storing keeps it fast and low-memory.
    zippable[item.filename] = [bytes, { level: 0 }];
    delivered += 1;
  }

  if (delivered === 0) return { delivered: 0, failed };

  const zipped = await zipAsync(zippable);
  saveBlob(zipped, zipName, "application/zip");
  return { delivered, failed };
}

function zipAsync(data: Zippable): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    zip(data, (err, out) => (err ? reject(err) : resolve(out)));
  });
}

async function fetchBytes(url: string, signal?: AbortSignal): Promise<Uint8Array> {
  // same-origin credentials: the mktg_auth session cookie MUST ride along —
  // signed-in curators download non-public files that the worker refuses to
  // anonymous requests ("omit" strips cookies even same-origin).
  const res = await fetch(toFetchUrl(url), { credentials: "same-origin", signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const buf = await res.arrayBuffer();
  return new Uint8Array(buf);
}

/**
 * Reduce a (possibly absolute) manifest URL to an origin-relative path so the
 * fetch is same-origin in prod and goes through the Vite /cdn proxy in dev.
 * Falls back to the original string if it isn't a parseable URL.
 */
export function toFetchUrl(url: string): string {
  try {
    const u = new URL(url, window.location.href);
    return u.pathname + u.search;
  } catch {
     
    console.warn("[download] could not parse asset URL, using as-is:", url);
    return url;
  }
}

function triggerDownload(href: string, filename: string): void {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function saveBlob(data: Uint8Array, filename: string, mime: string): void {
  // fflate returns Uint8Array<ArrayBufferLike>; the DOM's BlobPart type wants an
  // ArrayBuffer-backed view. The bytes are a standalone buffer, so this is safe.
  const blob = new Blob([data as unknown as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, filename);
  // Give the download a beat to start before releasing the object URL.
  setTimeout(() => URL.revokeObjectURL(url), 15000);
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** jpeg → jpg, webp → webp, etc. Mirrors the migrate-side variantFilename logic. */
export function extForFormat(format: string): string {
  return format === "jpeg" ? "jpg" : format;
}

/**
 * Build a filesystem-safe base name from an asset's display name.
 * Strips characters illegal on Windows/macOS and collapses whitespace.
 */
export function safeBaseName(name: string): string {
  const cleaned = name
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned.length > 0 ? cleaned : "asset";
}

/**
 * Assign collision-free filenames across a batch. Assets often share display
 * names (e.g. "0072"), so duplicates get a "-2", "-3" suffix before the ext.
 */
export function dedupeFilenames<T extends { base: string; ext: string }>(
  entries: T[],
): (T & { filename: string })[] {
  const used = new Map<string, number>();
  return entries.map((e) => {
    const key = `${e.base}.${e.ext}`.toLowerCase();
    const n = used.get(key) ?? 0;
    used.set(key, n + 1);
    const filename = n === 0 ? `${e.base}.${e.ext}` : `${e.base}-${n + 1}.${e.ext}`;
    return { ...e, filename };
  });
}
